import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ArrowLeft, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function GateSecurityLogin() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [form, setForm] = useState({ username: "", password: "" });
    const [busy, setBusy] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    async function handleLogin(e) {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/security-login", {
                username: form.username,
                password: form.password
            });
            
            setUser(data);
            toast.success(`Welcome, ${data.name}!`);
            navigate("/app/qr-scanner");
        } catch (error) {
            toast.error(formatError(error));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen bg-brand-bg flex flex-col">
            {/* Header */}
            <div className="bg-white border-b border-brand-line p-4">
                <div className="max-w-md mx-auto flex items-center justify-between">
                    <Logo size={56} />
                    <Link 
                        to="/login" 
                        className="text-brand-inkSoft hover:text-brand-ink text-sm flex items-center gap-1"
                    >
                        <ArrowLeft size={14} /> Main Login
                    </Link>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-md animate-fade-up">
                    {/* Badge */}
                    <div className="flex justify-center mb-6">
                        <div className="w-20 h-20 bg-brand-ink rounded-full flex items-center justify-center">
                            <ShieldCheck size={40} className="text-white" weight="fill" />
                        </div>
                    </div>

                    {/* Title */}
                    <div className="text-center mb-8">
                        <p className="text-xs uppercase tracking-overline text-brand-inkSoft mb-2">Gate Security</p>
                        <h1 className="font-heading text-3xl text-brand-ink tracking-tight">Security Login</h1>
                        <p className="text-sm text-brand-inkSoft mt-2">Enter your security guard credentials</p>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleLogin} className="bg-white border border-brand-line rounded-sm p-6 space-y-4">
                        <div className="space-y-2">
                            <Label>Username</Label>
                            <Input
                                required
                                autoFocus
                                type="text"
                                value={form.username}
                                onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
                                placeholder="Enter your username"
                                className="rounded-sm border-brand-line font-mono"
                                data-testid="security-username-input"
                                autoComplete="username"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Password</Label>
                            <div className="relative">
                                <Input
                                    required
                                    type={showPassword ? "text" : "password"}
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    placeholder="Enter your password"
                                    className="rounded-sm border-brand-line font-mono pr-10"
                                    data-testid="security-password-input"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-inkSoft hover:text-brand-ink"
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={busy}
                            data-testid="security-login-btn"
                            className="w-full rounded-full bg-brand-ink hover:bg-brand-inkSoft text-white"
                        >
                            {busy ? "Logging in..." : "Login to Gate Scanner"}
                        </Button>
                    </form>

                    {/* Help Text */}
                    <div className="mt-6 bg-brand-sage/30 border border-brand-action/20 rounded-sm p-4 text-sm text-brand-ink">
                        <p className="font-medium mb-2">Security Guard Access</p>
                        <ul className="text-xs text-brand-inkSoft space-y-1">
                            <li>• Use the username and password provided by admin</li>
                            <li>• You'll access the QR scanner after login</li>
                            <li>• Contact admin if you forgot your credentials</li>
                        </ul>
                    </div>

                    {/* Install PWA Hint */}
                    <div className="mt-4 text-center">
                        <p className="text-xs text-brand-inkSoft">
                            💡 Add to home screen for quick access
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="p-4 text-center text-xs text-brand-inkSoft">
                <p>maintayn Community OS • Gate Security Module</p>
            </div>
        </div>
    );
}
